import { Skeleton } from "@/components/ui/skeleton";
import { DigestPreviewDialog } from "./email/DigestPreviewDialog";
import { DigestScheduleTiles } from "./email/DigestScheduleTiles";
import { EmailActions } from "./email/EmailActions";
import { EmailSetupNotice } from "./email/EmailSetupNotice";
import { NotificationStatus } from "./email/NotificationStatus";
import { StateBadge } from "./email/StateBadge";
import { WeeklyPreviewDialog } from "./email/WeeklyPreviewDialog";
import { WeeklyStatus } from "./email/WeeklyStatus";
import { senderState, toggleState } from "./email/emailTokens";
import { useEmailPanel } from "./email/useEmailPanel";

/**
 * The email + chase reminder block on the dashboard overview.
 *
 * This file is deliberately only the shell: it resolves the three health
 * states from the status payload and hands each one to its own section, so
 * adding a new reminder never means touching the ones already working.
 */
export const EmailPanel = ({ onAuthError }) => {
  const { loading, status, busy, preview, weekly, setPreview, setWeekly, actions } =
    useEmailPanel(onAuthError);

  if (loading && !status) {
    return (
      <div className="tt-card mt-4 p-5" data-testid="email-panel-loading">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-3 h-20 w-full" />
      </div>
    );
  }
  if (!status) return null;

  const configured = Boolean(status.email?.configured);
  const notifications = status.notifications || {};
  const weeklySchedule = status.weekly || {};
  const clock = status.response_clock || {};

  // One place decides what the sending badge says, so the label, the tone and
  // the setup box below can never disagree about the state we're in. Resend
  // may still refuse our own domain, in which case customers can't be emailed
  // even though the key works and internal alerts are getting through.
  const sending = senderState({
    configured,
    testSender: Boolean(status.email?.using_test_sender),
    domainPending: Boolean(status.email?.domain_unverified),
  });
  const alerts = toggleState({
    enabled: notifications.enabled,
    configured: notifications.configured,
  });
  const recap = toggleState({ enabled: weeklySchedule.enabled, configured });

  return (
    <section className="tt-card mt-4 p-5" data-testid="email-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="tt-eyebrow">Email &amp; chase reminders</h2>
          <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Quote PDFs go to customers the moment they finish the calculator, and the team gets a
            chase list each weekday morning.
          </p>
        </div>
        <StateBadge state={sending} testId="email-panel-status-badge" />
      </div>

      {!sending.healthy ? (
        <EmailSetupNotice
          message={status.email?.message}
          requiredEnv={status.required_env}
          domainPending={Boolean(status.email?.domain_unverified)}
          lastChecked={status.email?.domain_checked_at}
          lastAnswer={status.email?.domain_detail}
          busy={busy === "domain-check"}
          onRecheck={actions.checkDomain}
        />
      ) : null}

      <DigestScheduleTiles schedule={status.digest || {}} last={status.last_digest} />
      <NotificationStatus notifications={notifications} state={alerts} />
      <WeeklyStatus schedule={weeklySchedule} clock={clock} state={recap} />
      <EmailActions configured={configured} busy={busy} actions={actions} />

      <DigestPreviewDialog preview={preview} onClose={() => setPreview(null)} />
      <WeeklyPreviewDialog preview={weekly} clock={clock} onClose={() => setWeekly(null)} />
    </section>
  );
};
